import { useState } from "react";
import { pipe } from "ramda";
import { UpdateInvoiceDetailsFormValues } from "../components/Forms/UpdateInvoiceDetailsForm";
import { createInvoiceDetailsSpec } from "../components/Invoices/invoiceDetailsSpec";
import { cleanObject } from "../lib/functions";
import {
  CreateInvoiceInput,
  Job,
  Mutation,
  MutationCreateInvoiceArgs,
  PossibleEjiService,
  Query,
  QueryCalculatePossibleEjiPriceInfoArgs,
  QueryGetJobArgs,
} from "../generated/nest-graphql";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { CREATE_INVOICE } from "../graphql/mutations/createInvoice";
import { useDispatch } from "../contexts/snackbar-context";
import { useHistory } from "react-router-dom";
import { showErrorAlert, showSuccessAlert } from "../actions";
import { parseTaxGateError, taxGateMessage } from "../lib/taxGate";
import { GET_JOB } from "../graphql/queries/getJob";
import { CALCULATE_POSSIBLE_EJI_PRICE_INFO } from "../graphql/queries/calculatePossibleEjiPriceInfo";
import {
  ejiPriceInfoToFormValue,
  formValuesToEJIDiscountInputs,
  formValuesToEJIPromoCodeInputs,
  formValuesToPossibleEJIServiceInputs,
  formValueToEJIPricingConfigInput,
  possibleEJIServicesToFormValuesSpec,
} from "../specs/ejiServicesSpec";
import { useEntityId } from "./useEntityId";

type result = {
  createJobInvoice: (jobId: string, values: UpdateInvoiceDetailsFormValues, onCompletedParams?: any) => Promise<void>;
};
export const useInvoice = (postInvoiceAction?: (invoiceId: string, otherParams?: any) => Promise<void>): result => {
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [createInvoice] = useMutation<Mutation, MutationCreateInvoiceArgs>(CREATE_INVOICE);
  const [calculatePossibleEjiPriceInfo] = useLazyQuery<Query, QueryCalculatePossibleEjiPriceInfoArgs>(
    CALCULATE_POSSIBLE_EJI_PRICE_INFO
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const jobId = useEntityId();
  const { data } = useQuery<Query, QueryGetJobArgs>(GET_JOB, {
    variables: {
      id: jobId,
    },
    fetchPolicy: "network-only",
    returnPartialData: true,
  });
  const job: Job = data?.getJob;

  const createJobInvoice = async (jobId: string, values: UpdateInvoiceDetailsFormValues, onCompletedParams?: any) => {
    if (creatingInvoice) return;
    setCreatingInvoice(true);
    try {
      const { data: priceData } = await calculatePossibleEjiPriceInfo({
        variables: {
          calculatePossibleEJIPriceInfoInput: {
            services: formValuesToPossibleEJIServiceInputs(values.services.filter((service) => service?.inInvoice)),
            discounts: formValuesToEJIDiscountInputs(values.discounts),
            pricingConfig: formValueToEJIPricingConfigInput(values.priceInfo?.pricingConfig),
            marketName: values.market,
            taxable: values.taxable,
            promoCodes: formValuesToEJIPromoCodeInputs(values.promoCodes),
            laborTaxRate: job?.priceInfo?.taxjarLaborTaxRate,
            partsTaxRate: job?.priceInfo?.taxjarPartsTaxRate,
            calculateAllServices: true,
          },
        },
      });
      let serviceStimateIdx = 0;
      const input = pipe(
        createInvoiceDetailsSpec,
        cleanObject
      )({
        ...values,
        priceInfo: pipe(() => priceData.calculatePossibleEJIPriceInfo.ejiPriceInfo, ejiPriceInfoToFormValue)(),
        services: pipe(
          () =>
            (values.services ?? [])
              .map((service, serviceIdx) => {
                let targetService;
                if (service.inInvoice) {
                  targetService = priceData.calculatePossibleEJIPriceInfo.ejiServices[serviceStimateIdx];
                  serviceStimateIdx++;
                }
                return {
                  ...service,
                  ...targetService,
                  items: service.items.map((item, itemIdx) => {
                    const targetItem = targetService?.items[itemIdx];
                    return {
                      ...item,
                      ...targetItem,
                    };
                  }),
                };
              })
              .filter((service) => service.inInvoice) as PossibleEjiService[],
          possibleEJIServicesToFormValuesSpec
        )(),
      });
      const invoiceInput: CreateInvoiceInput = input as CreateInvoiceInput;
      if (invoiceInput.preJobCheckListV2) {
        delete invoiceInput.preJobCheckListV2["licensePlate"];
        delete invoiceInput.preJobCheckListV2["licensePlateState"];
      }

      const result = await createInvoice({
        variables: {
          // @ts-ignore
          createInvoiceInput: invoiceInput,
        },
        onCompleted: (data) => {
          if (postInvoiceAction) {
            postInvoiceAction(data.createInvoice.id, onCompletedParams);
          }
        },
        update: (cache, { data: { createInvoice } }) => {
          const data = cache.readQuery<Query, QueryGetJobArgs>({
            variables: {
              id: jobId,
            },
            query: GET_JOB,
          });
          console.log("data", data);
          const getJob = data?.getJob;
          const objectToPlace = {
            getJob: {
              ...getJob,
              invoice: createInvoice,
            },
          };
          cache.writeQuery({
            query: GET_JOB,
            variables: {
              id: jobId,
            },
            data: objectToPlace,
          });
        },
      });
      const id = result.data.createInvoice.id;
      showSuccessAlert(dispatch, "Success");
      await history.push(`/invoices/${id}`);
    } catch (error) {
      // Proactive tax gate: the job's tax couldn't be confirmed (missing address, TaxJar
      // down, etc.) so the invoice wasn't created. Surface the specific reason in the
      // existing toast so the tech knows what's wrong instead of a silent/opaque failure.
      const taxGate = parseTaxGateError(error);
      if (taxGate) {
        showErrorAlert(dispatch, taxGateMessage(taxGate, "invoice"), { persist: true });
        return;
      }
      throw error;
    } finally {
      setCreatingInvoice(false);
    }
  };

  return {
    createJobInvoice,
  };
};
